/**
 * User Service
 * Handles API requests related to tenant user management
 */
import apiClient from './apiClient';

const userService = {
  /**
   * Get tenant users
   * @returns {Promise} - Response with tenant user list
   */
  getTenantUsers() {
    return apiClient.get('/api/supplier/user-profile/tenant-users')
      .then(response => response.data);
  },

  /**
   * Get user by ID
   * @param {string} userId - User ID
   * @returns {Promise} - Response with user details
   */
  getUserById(userId) {
    if (!userId) {
      console.error('getUserById: userId is required');
      return Promise.reject(new Error('User ID is required'));
    }
    
    return apiClient.get(`/api/supplier/user-profile/${userId}`)
      .then(response => response.data);
  },

  /**
   * Get tenant user by ID
   * @param {string} userId - User ID
   * @returns {Promise} - Response with tenant user details
   */
  getTenantUserById(userId) {
    if (!userId) {
      console.error('getTenantUserById: userId is required');
      return Promise.reject(new Error('User ID is required'));
    }
    
    return apiClient.get(`/api/supplier/user-profile/${userId}/tenant-user`)
      .then(response => response.data);
  },

  /**
   * Create new user
   * @param {Object} userData - User data to create
   * @returns {Promise} - Response with created user
   */
  createUser(userData) {
    if (!userData) {
      console.error('createUser: userData is required');
      return Promise.reject(new Error('User data is required'));
    }

    return apiClient.post('/api/supplier/user-profile/user', userData)
      .then(response => response.data);
  },

  /**
   * Update user
   * @param {string} userId - User ID to update
   * @param {Object} userData - Updated user data
   * @returns {Promise} - Response with updated user
   */
  updateUser(userId, userData) {
    if (!userId) {
      console.error('updateUser: userId is required');
      return Promise.reject(new Error('User ID is required'));
    }

    if (!userData) {
      console.error('updateUser: userData is required');
      return Promise.reject(new Error('User data is required'));
    }

    // Include user ID in the request body
    const updateData = {
      ...userData,
      id: userId
    };

    return apiClient.put('/api/supplier/user-profile/tenant-user', updateData)
      .then(response => response.data);
  },

  /**
   * Delete user
   * @param {string} userId - User ID to delete
   * @returns {Promise} - Response with deletion result
   */
  deleteUser(userId) {
    if (!userId) {
      console.error('deleteUser: userId is required');
      return Promise.reject(new Error('User ID is required'));
    }

    return apiClient.delete(`/api/supplier/user-profile/${userId}/tenant-user`)
      .then(response => response.data);
  },

  /**
   * Toggle user active status
   * @param {string} userId - User ID
   * @param {boolean} isActive - New active status
   * @returns {Promise} - Response with updated user
   */
  toggleUserStatus(userId, isActive) {
    if (!userId) {
      console.error('toggleUserStatus: userId is required');
      return Promise.reject(new Error('User ID is required'));
    }

    return apiClient.patch(`/api/supplier/user-profile/${userId}/status`, {
      isActive
    }).then(response => response.data);
  },

  /**
   * Get users available for impersonation (Super Admin only)
   * @param {string} searchQuery - Optional search query to filter users
   * @returns {Promise} - Response with users available for impersonation
   */
  getUsersForImpersonation(searchQuery = '') {
    const params = searchQuery ? { search: searchQuery } : {};
    
    return apiClient.get('/api/supplier/account/tenants-for-impersonation', { params })
      .then(response => response.data);
  },

  /**
   * Send password setup email to user
   * @param {string} email - User email address
   * @returns {Promise} - Response with email sending result
   */
  sendPasswordSetupEmail(email) {
    if (!email) {
      console.error('sendPasswordSetupEmail: email is required');
      return Promise.reject(new Error('Email is required'));
    }

    return apiClient.post('/api/supplier/account/send-password-setup-email', { email })
      .then(response => response.data);
  }
};

export default userService; 